import type { ActivityCompletionMetrics } from "@/app/integration/contracts";
import {
  getSchoolbagFeedbackCue,
  SCHOOLBAG_PROGRESS_CUE
} from "@/audio/curriculum-catalog/habits-feedback-cues";
import { createLevelDefinitionSchema } from "@/domain/schemas";
import {
  calculateDragOrganizeScore,
  createDragOrganizeState,
  dragOrganizePayloadSchema,
  reduceDragOrganize,
  type DragOrganizePayload
} from "@/game/templates/drag-organize";

import {
  requireActivityType,
  requireHabitsAudioPath,
  requirePlayerMedia
} from "./binding-utils";
import type {
  HabitsFeedbackModel,
  HabitsPlayerLevel,
  SchoolbagAction,
  SchoolbagBinding,
  SchoolbagState
} from "./types";

const schoolbagLevelSchema = createLevelDefinitionSchema(
  dragOrganizePayloadSchema
);

function getMetrics(
  state: SchoolbagState,
  payload: DragOrganizePayload
): ActivityCompletionMetrics {
  const score = calculateDragOrganizeScore(state, payload);

  return {
    completed: score.completed,
    score: score.points,
    correctRate: score.correctRate,
    hintCount: score.hintCount,
    retryCount: score.retryCount
  };
}

function getFeedback(
  state: SchoolbagState
): HabitsFeedbackModel | null {
  if (state.feedback === null) {
    return null;
  }

  if (state.feedback.kind === "success") {
    return {
      audioPath: SCHOOLBAG_PROGRESS_CUE.audioPath,
      kind: "success",
      playbackRequest: state.feedbackPlaybackRequest,
      text: SCHOOLBAG_PROGRESS_CUE.text
    };
  }

  const isRequestedHint = state.feedbackSource === "hint-request";
  const kind = isRequestedHint ? "hint" : "retry";

  const cue = getSchoolbagFeedbackCue(kind, state.feedback.hintStage);

  return {
    audioPath: cue.audioPath,
    kind,
    playbackRequest: state.feedbackPlaybackRequest,
    text: cue.text
  };
}

function createSchoolbagState(): SchoolbagState {
  return {
    ...createDragOrganizeState(),
    feedbackPlaybackRequest: 0,
    feedbackSource: null
  };
}

function reduceSchoolbag(
  state: SchoolbagState,
  payload: DragOrganizePayload,
  action: SchoolbagAction
): SchoolbagState {
  if (action.type === "replay") {
    return createSchoolbagState();
  }

  const nextState = reduceDragOrganize(state, payload, action);

  if (nextState === state) {
    return state;
  }

  return {
    ...nextState,
    feedbackPlaybackRequest:
      nextState.feedback !== null && nextState.feedback !== state.feedback
        ? state.feedbackPlaybackRequest + 1
        : state.feedbackPlaybackRequest,
    feedbackSource:
      action.type === "request-hint"
        ? "hint-request"
        : nextState.feedback?.kind === "retry"
          ? "placement-retry"
          : null
  };
}

function getItemLabel(
  payload: DragOrganizePayload,
  itemId: string
): string {
  const item = payload.items.find(({ id }) => id === itemId);

  if (item === undefined) {
    throw new Error("Schoolbag placement references missing item " + itemId);
  }

  return item.label;
}

function createAdapter(
  level: HabitsPlayerLevel<DragOrganizePayload>
): SchoolbagBinding["adapter"] {
  return {
    activityType: "pack-schoolbag",
    parsePayload: (value) => dragOrganizePayloadSchema.parse(value),
    createInitialState: (payload, context) => {
      void payload;
      void context;
      return createSchoolbagState();
    },
    reduce: (state, payload, action, context) => {
      void context;
      return reduceSchoolbag(state, payload, action);
    },
    getMetrics,
    toRenderModel: (state, payload) => {
      const metrics = getMetrics(state, payload);

      return {
        difficulty: level.difficulty,
        instruction: {
          audioPath: level.promptAudio,
          caption: level.instruction
        },
        levelId: level.id,
        sceneImagePath: level.assets[0],
        title: level.title,
        phase: state.completed ? "result" : "play",
        selectedItemId: state.selectedItemId,
        items: payload.items.map((item) => {
          const placement = state.placements.find(
            ({ itemId }) => itemId === item.id
          );
          const dropZone = payload.dropZones.find(
            ({ id }) => id === placement?.dropZoneId
          );

          return {
            audioPath: requireHabitsAudioPath(
              item.audio,
              level.id,
              "schoolbag item " + item.id
            ),
            disabled: state.completed || placement !== undefined,
            id: item.id,
            label: item.label,
            placedInDropZoneId: placement?.dropZoneId ?? null,
            placedInDropZoneLabel: dropZone?.label ?? null,
            selected: state.selectedItemId === item.id
          };
        }),
        dropZones: payload.dropZones.map((dropZone) => ({
          audioPath: requireHabitsAudioPath(
            dropZone.audio,
            level.id,
            "schoolbag drop zone " + dropZone.id
          ),
          enabled: !state.completed && state.selectedItemId !== null,
          id: dropZone.id,
          label: dropZone.label,
          placedItems: state.placements
            .filter(({ dropZoneId }) => dropZoneId === dropZone.id)
            .map(({ itemId }) => ({
              id: itemId,
              label: getItemLabel(payload, itemId)
            }))
        })),
        feedback: getFeedback(state),
        canUndo: state.placements.length > 0,
        canRequestHint: !state.completed,
        result: state.completed ? metrics : null
      };
    }
  };
}

export function createSchoolbagBinding(
  sourceLevel: unknown
): SchoolbagBinding {
  const level = requirePlayerMedia(
    schoolbagLevelSchema.parse(sourceLevel)
  );
  requireActivityType(level, "pack-schoolbag");

  return {
    activityType: "pack-schoolbag",
    level,
    adapter: createAdapter(level)
  };
}
